/**
 * Cart icon: keeps the header bubble in sync with cart.item_count on cart:update (shared by CartAdd/CartUpdate), from the count the event carries, else by refetching /cart.js.
 * @typedef {Object} CartIconRefs
 * @property {HTMLElement} bubble
 * @property {HTMLElement} count
 * @extends {Component<CartIconRefs>}
 */

import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

class CartIconComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#refresh);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#refresh);
  }

  #refresh = async (event) => {
    const count = this.#countFromEvent(event);
    if (count !== null) {
      this.#setCount(count);
      return;
    }

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;

      const cart = await response.json();
      this.#setCount(cart.item_count);
    } catch (error) {
      console.error("[cart-icon] refresh failed:", error);
    }
  };

  /** Count carried by the event: explicit (cart/change.js returns the cart), else read from the bundled drawer HTML (cart/add.js only returns the added items). */
  #countFromEvent(event) {
    const data = event?.detail?.data;
    if (Number.isInteger(data?.itemCount)) return data.itemCount;

    for (const html of Object.values(data?.sections || {})) {
      const match = /data-cart-item-count="(\d+)"/.exec(html || "");
      if (match) return Number(match[1]);
    }
    return null;
  }

  #setCount(count) {
    const { bubble, count: countEl } = this.refs;
    if (!bubble || !countEl) return;

    countEl.textContent = String(count);
    // `hidden` hides whatever its value is: setAttribute("hidden", "false") would keep the bubble hidden for good.
    bubble.hidden = count === 0;
  }
}

if (!customElements.get("cart-icon-component")) {
  customElements.define("cart-icon-component", CartIconComponent);
}
